// Office start-up after the role check: the staff list (and the user's name), the first draw of
// the Work Orders and Operations lists, the data listener, and the background loads; offline, the
// device copies only. Moved out of app.js unchanged (docs/split-plan.md, session 7).
import { setAppMode } from './appMode';
import { getCustomerOpen, getInventory, getInventoryPOs, getJobsClosed, getJobsOpen, getMapData, getMooringDatabaseOpen, getOpCodes, getReceiving, getRentalDatabaseOpen, getVendors } from './deviceSync';
import { isFirebaseConnected } from './initFirebase';
import { fetchParts } from './laborParts';
import { renderLaborData, renderPartsData } from './quickSelect';
import { bootSwitchView, otherPageOnScreen } from './shellAuth';
import { loadSecondaryIframesLazy } from './shellPages';
import { rememberTechs } from './techNames';
import { initializeWeekStart } from './techWeek';
import { fetchFirebaseLastUpdated, fetchIdToken, fetchIndexedDBLastUpdated, getDB, getUserRole, isAdminRole } from './utilities';

let shell = null;
export function setShellBootDeps(deps) { shell = deps; }

let hasRenderedInitialUI = false;          // ← module-level guard
let warmInitStarted = false;


async function renderInitialUI() {
  if (hasRenderedInitialUI) return;        // already drew once → skip
  hasRenderedInitialUI = true;

  // Reveal the container immediately so the skeleton rows are visible during load — unless
  // the user has already opened another page from the rail while the first sync ran; then
  // the list still builds, just out of sight, instead of jumping them back to Work Orders.
  if (!otherPageOnScreen()) {
    document.getElementById('open-work').style.display = 'flex';
    document.getElementById('open-work').style.visibility = 'visible';
    document.getElementById('current-page').textContent = 'Work Orders';
  }

  await shell.mainTable();                       // big table at left
  await shell.populateJobList();                 // big list at right
}

let initialRenderPromise = null;

function ensureInitialUI() {
  if (hasRenderedInitialUI) return Promise.resolve();
  if (initialRenderPromise) return initialRenderPromise;

  initialRenderPromise = (async () => {
    await renderInitialUI();
    hasRenderedInitialUI = true;
  })();

  return initialRenderPromise;
}





// The staff list, then techs' on-screen names ("Rafael M.", techNames.js) from it.
export async function collectTechInformation() {
  await loadTechInformation();
  if (shell.globalUserData) rememberTechs(shell.globalUserData);
}

async function loadTechInformation() {

  try {
const db = await getDB();

    if (isFirebaseConnected()) {
      const firebaseTimestamp = await fetchFirebaseLastUpdated('users');
      const indexedDBTimestamp = await fetchIndexedDBLastUpdated(db, 'techDataLastUpdated');

      if (!indexedDBTimestamp || indexedDBTimestamp < firebaseTimestamp) {
        const idToken = await fetchIdToken();
        const firebaseUrl = `${shell.currentURL}/users.json?auth=${idToken}`;

        const options = {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        };

        const controller = new AbortController();
        const fetchTimeout = setTimeout(() => controller.abort(), 8000);
        let response;
        try {
          response = await fetch(firebaseUrl, { ...options, signal: controller.signal });
        } finally {
          clearTimeout(fetchTimeout);
        }
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();

        shell.globalUserData = result;

        console.log('Tech information fetched from Firebase:', result);
        const currentUserRecord = shell.globalUserData[localStorage.getItem('currentUserId')];
        if (!currentUserRecord) {
          console.error('Current user not found in shell.globalUserData');
          return;
        }
        const techRole = currentUserRecord.role;

        document.getElementById('currentUserName').textContent = shell.globalUserData[localStorage.getItem('currentUserId')].displayName;
        if(!isAdminRole(techRole)){
          bootSwitchView('job');
        }

        const techUsers = Object.entries(result).reduce((acc, [id, userData]) => {
          if (id === 'lastUpdated') return acc;
          // role is a free-form comma list ("tech, yard", "admin, tech") — a
          // strict === 'tech' check silently drops every compound-role staff
          // member from the tech roster/scheduler, so this needs a token check.
          const roleTokens = (userData.role || '').split(',').map(s => s.trim().toLowerCase());
          if (roleTokens.includes('tech') || isAdminRole(userData.role)) {
            acc[id] = { id, ...userData };
          }
          return acc;
        }, {});

        const transaction = db.transaction(['techData', 'metadata'], 'readwrite');
        const techStore = transaction.objectStore('techData');
        const metadataStore = transaction.objectStore('metadata');

        const indexedDBTechIDs = await new Promise((resolve, reject) => {
          const request = techStore.getAllKeys();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        const firebaseTechIDs = Object.keys(techUsers);

        Object.values(techUsers).forEach(tech => {
          techStore.put(tech);
        });

        const deletedTechIDs = indexedDBTechIDs.filter(id => !firebaseTechIDs.includes(id));
        deletedTechIDs.forEach(id => techStore.delete(id));

        metadataStore.put({ key: 'techDataLastUpdated', timestamp: firebaseTimestamp });

        await new Promise((resolve, reject) => {
          transaction.oncomplete = () => {
            console.log('All tech data have been updated in IndexedDB.');
            resolve();
          };
          transaction.onerror = (event) => {
            console.error('Transaction error:', event.target.errorCode);
            reject(event.target.error);
          };
        });
      } else {
        // If data is up to date, fetch from IndexedDB
        const transaction = db.transaction(['techData'], 'readonly');
        const techStore = transaction.objectStore('techData');
        const techUsers = await new Promise((resolve, reject) => {
          const request = techStore.getAll();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
        shell.globalUserData = techUsers.reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {});

        const cachedUserRecord = shell.globalUserData[localStorage.getItem('currentUserId')];
        if (cachedUserRecord) {
          document.getElementById('currentUserName').textContent = cachedUserRecord.displayName;
          const techRole = cachedUserRecord.role;
          if(!isAdminRole(techRole)){
            bootSwitchView('job');
          }
        }
      }
    } else {
      // Offline: fetch from IndexedDB
      const transaction = db.transaction(['techData'], 'readonly');
      const techStore = transaction.objectStore('techData');
      const techUsers = await new Promise((resolve, reject) => {
        const request = techStore.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      shell.globalUserData = techUsers.reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {});
      document.getElementById('currentUserName').textContent = shell.globalUserData[localStorage.getItem('currentUserId')].displayName;
      const techRole = shell.globalUserData[localStorage.getItem('currentUserId')].role;
        if(!isAdminRole(techRole)){
          bootSwitchView('job');
        }
    }

  } catch (error) {
    console.error("Error collecting tech information:", error);
  }
}

export async function onlineFlow(user) {
  try {
    const hasAccess = await getUserRole(user.uid);
    if (!hasAccess) {
      document.getElementById('no-role').style.display = "flex";
      return;
    }

    if (hasAccess === 'dock') {
      // Launch drivers and dock hands have their own app (fuel, launch checks, drills, clock).
      window.location.replace('/dock.html');
      return;
    }
    if (hasAccess === 'dock-legacy-fuel-only') {
      document.getElementById('fuel-reading').innerHTML = '<iframe src="/fuel" class="page-frame"></iframe>';
      document.querySelectorAll('.page-view').forEach(page => {
        page.style.display = 'none';
        page.style.visibility = 'hidden';
      });
      document.getElementById('fuel-reading').style.display = 'flex';
      document.getElementById('fuel-reading').style.visibility = 'visible';
      document.getElementById('current-page').innerHTML = "Fuel Reading";
      document.querySelectorAll('.remove').forEach(button => button.remove());
      document.getElementById('buttonBar').style.display = 'flex';
      return;
    }

    setAppMode({ readOnly: false });

    document.getElementById('buttonBar').style.display = 'flex';
    await collectTechInformation();
    initializeWeekStart();

    await getDB();
    await getJobsOpen();
    getCustomerOpen();

    // Capture the promise from this one call rather than calling
    // ensureInitialUI() again below — renderInitialUI() flips
    // hasRenderedInitialUI synchronously at its own start (before awaiting
    // mainTable()), so a second ensureInitialUI() call made on the same
    // tick sees that flag already true and short-circuits to
    // Promise.resolve() immediately, firing checkFollowUpDue() before
    // mainTable() has actually populated jobsByWorkOrder.
    const initialUIPromise = ensureInitialUI();
    if (typeof shell.checkFollowUpDue === 'function') {
      initialUIPromise.then(() => shell.checkFollowUpDue());
    }

    loadSecondaryIframesLazy();
    shell.initializeDataListener();
    initializeDatabaseWarm();
    document.getElementById('currentUser').value = user.displayName;
  } catch (error) {
    console.error("Error during online flow:", error);
  }
}


export async function offlineFlow(user) {
  try {
    console.log('Offline flow initiated');
    setAppMode({ readOnly: true });

    document.getElementById('buttonBar').style.display = 'flex';
    await loadDataFromIndexedDB();
    loadSecondaryIframesLazy();
    document.getElementById('currentUser').value = user.displayName;
    // Get user info from localStorage
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    if (userInfo) {
      document.getElementById('currentUser').value = userInfo.displayName;
    }

    // Additional offline operations...
  } catch (error) {
    console.error("Error during offline flow:", error);
  }
}

async function initializeDatabaseWarm() {
  
  if (warmInitStarted) return;
  warmInitStarted = true;

  try {
    await Promise.all([
      getOpCodes(),
      getMapData(),
      getInventory(),
      getInventoryPOs(),
      getReceiving(),
      getVendors(),
      getMooringDatabaseOpen(),
      getRentalDatabaseOpen(),
      getJobsClosed()
    ]);
    Promise.all([shell.populateVendorLists(), fetchParts()]).then(() => {
      Promise.all([renderLaborData(), renderPartsData()]);
    });
  } catch (error) {
    console.error('Warm init failed:', error);
  }
}


async function loadDataFromIndexedDB() {
  try {
    await Promise.all([
      getJobsOpen(),
      getCustomerOpen(),
      getOpCodes(),
      getMapData(),
      getInventory(),
      getInventoryPOs(),
      getReceiving(),
      getVendors(),
      getMooringDatabaseOpen(),
      getRentalDatabaseOpen(),
      shell.populateVendorLists(),
      getJobsClosed()
    ])
    console.log("Loading data from IndexedDB...");
  } catch (error) {
    console.error('Error loading data from IndexedDB', error);
  }
}
